import { Button, CheckboxCards, Flex, Text } from "@radix-ui/themes";
import { useForm, usePage } from "@inertiajs/react";
import { type SubmitEvent } from "react";
import type { SharedProps } from "@/types";
import OnboardingShell from "@/shared/onboarding/OnboardingShell";
import { onboardingLayout } from "@/layouts/OnboardingLayout";
import type {
  OnboardingIntent,
  OnboardingIntentsProps,
} from "@/types/onboarding";

// Keys match User::INTENTS; the server rejects anything else.
const OPTIONS: {
  value: OnboardingIntent;
  title: string;
  description: string;
}[] = [
  {
    value: "media",
    title: "I shoot photo, video or drone",
    description: "Apply to media days and get credentialed at tracks.",
  },
  {
    value: "driver",
    title: "I drive",
    description: "Add your car, apply to drive and log the events you ran.",
  },
  {
    value: "find",
    title: "I want to find tracks and events",
    description: "See what's on near you and follow tracks.",
  },
];

export default function OnboardingIntents({
  intents,
  ...shell
}: OnboardingIntentsProps) {
  const { data, setData, patch, processing } = useForm<{ intents: string[] }>({
    intents,
  });
  const { brand } = usePage<SharedProps>().props;
  const siteName = brand?.name ?? "Driftables";
  // On a branded site they've already found the track (or club), so finding
  // tracks and events isn't on offer there.
  const options = brand
    ? OPTIONS.filter((option) => option.value !== "find")
    : OPTIONS;

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    patch("/welcome/intents");
  };

  return (
    <OnboardingShell
      {...shell}
      title={`What brings you to ${siteName}?`}
      subtitle="Pick everything that fits. We'll set up what you need and order your home page around it."
    >
      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap="5">
          <CheckboxCards.Root
            size="3"
            columns="1"
            value={data.intents}
            onValueChange={(value) => setData("intents", value)}
          >
            {options.map((option) => (
              <CheckboxCards.Item key={option.value} value={option.value}>
                <Flex direction="column" gap="1">
                  <Text weight="bold">{option.title}</Text>
                  <Text size="2" color="gray">
                    {option.description}
                  </Text>
                </Flex>
              </CheckboxCards.Item>
            ))}
            <CheckboxCards.Item value="team" disabled>
              <Flex direction="column" gap="1">
                <Text weight="bold">I'm on a team</Text>
                <Text size="2" color="gray">
                  Coming soon.
                </Text>
              </Flex>
            </CheckboxCards.Item>
          </CheckboxCards.Root>
          <Flex justify="end">
            <Button
              size="3"
              type="submit"
              disabled={processing || data.intents.length === 0}
            >
              Continue
            </Button>
          </Flex>
        </Flex>
      </form>
    </OnboardingShell>
  );
}

OnboardingIntents.layout = onboardingLayout;
